import privacyMarkdown from './privacy-policy.md?raw'
import refundMarkdown from './refund-policy.md?raw'
import termsMarkdown from './terms-of-service.md?raw'

/**
 * Laplace GTM legal documents. The Markdown files are the single source:
 * the pages render them as they are, and they are what goes to review.
 * English only — the product is sold in the US.
 */
export type LegalDocumentKey = 'terms' | 'privacy' | 'refund'

export type LegalSection = {
  id: string
  title: string
  markdown: string
}

export type LegalDocument = {
  key: LegalDocumentKey
  title: string
  updated: string | null
  intro: string
  sections: ReadonlyArray<LegalSection>
}

/** Site paths of the documents, after `/{lang}/`; Markdown links use them as is. */
export const LEGAL_DOCUMENT_PATHS: Record<LegalDocumentKey, string> = {
  terms: 'terms',
  privacy: 'privacy',
  refund: 'refund-policy',
}

export const LEGAL_OPERATOR =
  'Individual Entrepreneur Aleksandr Nekrashenko (Georgia)'
export const LEGAL_CONTACT_EMAIL = 'support@laplace-gtm.com'

const MARKDOWN: Record<LegalDocumentKey, string> = {
  terms: termsMarkdown,
  privacy: privacyMarkdown,
  refund: refundMarkdown,
}

/** "7. Your choices and rights" → "7-your-choices-and-rights" (GitHub-style). */
export function legalSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

/**
 * Splits a document into its title (`# `), the "Last updated" line, the
 * intro before the first `## ` heading, and one section per `## ` heading.
 */
export function parseLegalDocument(
  key: LegalDocumentKey,
  markdown: string,
): LegalDocument {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  let title = ''
  let updated: string | null = null
  const intro: Array<string> = []
  const sections: Array<{ title: string; lines: Array<string> }> = []

  for (const line of lines) {
    if (!title && line.startsWith('# ')) {
      title = line.slice(2).trim()
      continue
    }
    const updatedMatch = /^_Last updated: (.+)_$/.exec(line.trim())
    if (updatedMatch && sections.length === 0) {
      updated = updatedMatch[1]
      continue
    }
    if (line.startsWith('## ')) {
      sections.push({ title: line.slice(3).trim(), lines: [] })
      continue
    }
    const current = sections.at(-1)
    if (current) current.lines.push(line)
    else intro.push(line)
  }

  return {
    key,
    title,
    updated,
    intro: intro.join('\n').trim(),
    sections: sections.map((section) => ({
      id: legalSlug(section.title),
      title: section.title,
      markdown: section.lines.join('\n').trim(),
    })),
  }
}

export function getLegalDocument(key: LegalDocumentKey): LegalDocument {
  return parseLegalDocument(key, MARKDOWN[key])
}
